import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { useDashboard } from "../hooks/useDashboard";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Building2,
  DollarSign,
  Package,
  Calendar,
  TrendingUp,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ChevronRight,
} from "lucide-react";
import {
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  readDashboardStats,
  readCommandCenter,
  scheduleAdherence,
  spendHistory,
  budgetPosition,
  measurementDetail,
  projectProgress,
  countLabel,
} from "../features/command/commandPresentation";
import {
  CommandStat,
  CommandSubStat,
  AbsenceNotice,
  SummaryRefusal,
  ProjectProgress,
} from "../features/command/CommandCards";

const COLORS = ["#1CBCF8", "#9A60F2", "#F59E0B", "#10B981", "#EC4899"];

const DashboardPage = () => {
  const { tenant } = useAuth();
  const navigate = useNavigate();
  const { stats, recentProjects, projectStages, commandCenter, riskQueue, loading } = useDashboard();

  /**
   * Every figure on this page now comes through a contract reader.
   *
   * The page used to read `stats?.x || 0` in thirteen places, which collapsed
   * three different facts — the backend said zero, the backend said nothing,
   * and the request has not resolved — into one glyph. It also drew a
   * six-month "Budget vs Actual Spend" curve from numbers written into this
   * file, and printed "100% On Schedule" from a backend constant that carries
   * a TODO beside it.
   *
   * MISSING IS NOT ZERO. MALFORMED IS NOT EMPTY. And a number OMNΛS cannot
   * measure is not a measurement. See features/command/commandPresentation.js.
   */
  const summary = readDashboardStats(stats);
  const centre = readCommandCenter(commandCenter);
  const budget = budgetPosition(summary.stats);
  const schedule = scheduleAdherence(summary.stats?.scheduleAdherence);
  const spend = spendHistory();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary text-xl">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-heading tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          Welcome back to {tenant?.name || "OMNΛS Construction"}
        </p>
      </div>

      {/* Stats Grid */}
      {summary.readable ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <CommandStat
            testId="stat-projects"
            label="Total Projects"
            fact={summary.stats.totalProjects}
            icon={<Building2 className="h-6 w-6 text-primary" />}
            sub={
              <span className="flex items-center gap-1">
                <TrendingUp size={14} className="text-emerald-400" />
                <CommandSubStat
                  testId="stat-projects-active"
                  fact={summary.stats.activeProjects}
                  suffix="active"
                  tone="text-emerald-400"
                />
              </span>
            }
          />

          <CommandStat
            testId="stat-budget"
            label="Total Budget"
            fact={summary.stats.totalBudget}
            kind="currency"
            icon={<DollarSign className="h-6 w-6 text-secondary" />}
            sub={
              <div className="space-y-1">
                {measurementDetail(summary.stats.totalBudget, "budget") ? (
                  <p className="text-xs text-muted-foreground" data-testid="stat-budget-coverage">
                    {measurementDetail(summary.stats.totalBudget, "budget")}
                  </p>
                ) : null}
                <CommandSubStat
                  testId="stat-budget-spent"
                  fact={summary.stats.totalSpent}
                  kind="currency"
                  suffix="spent"
                />
                {measurementDetail(summary.stats.totalSpent, "actual cost") ? (
                  <p className="text-xs text-muted-foreground" data-testid="stat-spent-coverage">
                    {measurementDetail(summary.stats.totalSpent, "actual cost")}
                  </p>
                ) : null}
              </div>
            }
          />

          <CommandStat
            testId="stat-procurement"
            label="Procurement"
            fact={centre.readable ? centre.center.procurementTotal : null}
            icon={<Package className="h-6 w-6 text-amber-500" />}
            sub={
              <span className="flex items-center gap-1">
                <Clock size={14} className="text-amber-400" />
                <CommandSubStat
                  testId="stat-procurement-pending"
                  fact={centre.readable ? centre.center.procurementPending : null}
                  suffix="pending"
                  tone="text-amber-400"
                />
              </span>
            }
          />

          <CommandStat
            testId="stat-activities"
            label="Activities"
            fact={centre.readable ? centre.center.activityTotal : null}
            icon={<Calendar className="h-6 w-6 text-emerald-500" />}
            sub={
              <span className="flex items-center gap-1">
                <CheckCircle2 size={14} className="text-emerald-400" />
                <CommandSubStat
                  testId="stat-activities-complete"
                  fact={centre.readable ? centre.center.activityComplete : null}
                  suffix="completed"
                  tone="text-emerald-400"
                />
              </span>
            }
          />
        </div>
      ) : (
        <SummaryRefusal reason={summary.reason} />
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/*
          WHERE THE FABRICATED SPEND CURVE USED TO BE.

          This slot held a "Budget vs Actual Spend" area chart drawn from six
          months written into this file. There is no series behind it:
          dashboardService requests none, and routes/dashboard.py:43-49 sums
          Project.budget and Project.actual_cost into two SCALARS. A curve
          cannot be drawn from two totals.

          The absence is stated rather than left blank, because a missing chart
          reads as a chart that failed to load, and an operator who thinks it
          broke will go looking for it.
        */}
        <div className="lg:col-span-2">
          <AbsenceNotice
            testId="spend-history"
            title="Budget over time"
            headline={spend.headline}
            detail={spend.detail}
          />
        </div>

        {/* Project Stages Pie Chart */}
        <Card className="stat-card" data-testid="chart-project-stages">
          <CardHeader>
            <CardTitle className="font-heading">Project Stages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {projectStages.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={projectStages}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {projectStages.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#151725",
                        border: "1px solid #2D3748",
                        borderRadius: "8px",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  No project data yet
                </div>
              )}
            </div>
            {projectStages.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-4 justify-center">
                {projectStages.map((stage, index) => (
                  <div key={stage.name} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-sm text-muted-foreground capitalize">
                      {stage.name}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Command Center */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="font-heading">Bid Deadlines</CardTitle>
          </CardHeader>
          <CardContent>
            {(commandCenter?.upcoming_bid_deadlines || []).length > 0 ? (
              <div className="space-y-3">
                {commandCenter.upcoming_bid_deadlines.slice(0, 5).map((bid) => (
                  <div key={bid.id} className="flex items-center justify-between rounded-lg bg-muted/30 p-3">
                    <div>
                      <p className="font-medium">{bid.title}</p>
                      <p className="text-xs text-muted-foreground">{bid.project_name || "Unassigned"}</p>
                    </div>
                    <div className="text-right text-sm">
                      <p className="capitalize">{bid.status}</p>
                      <p className="text-muted-foreground">{bid.due_date ? new Date(bid.due_date).toLocaleDateString() : "No due date"}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No upcoming bid deadlines.</p>
            )}
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="font-heading">Schedule Watch</CardTitle>
          </CardHeader>
          <CardContent>
            {(commandCenter?.schedule_risks || []).length > 0 ? (
              <div className="space-y-3">
                {commandCenter.schedule_risks.slice(0, 5).map((item) => (
                  <div key={item.id} className="flex items-center justify-between rounded-lg bg-muted/30 p-3">
                    <div>
                      <p className="font-medium">{item.name}</p>
                      <p className="text-xs text-muted-foreground">{item.project_name || "Project"}</p>
                    </div>
                    <span className="text-xs rounded-full bg-amber-500/20 text-amber-400 px-2 py-1 capitalize">{item.status?.replace(/_/g, " ")}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No schedule items need attention.</p>
            )}
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="font-heading">MVP Readiness</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Pending approvals</span>
              <span className="font-bold" data-testid="readiness-approvals">
                {countLabel(centre.readable ? centre.center.pendingApprovals : null)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Documents</span>
              <span className="font-bold" data-testid="readiness-documents">
                {countLabel(centre.readable ? centre.center.documentCount : null)}
              </span>
            </div>
            {/*
              Scope AI and Risk alerts used to fall back to the literal strings
              "available" and "planned" when the backend said nothing — the
              screen announcing a capability state, and a roadmap, that no
              service had reported. A capability nobody stated is not a
              capability, so the rows are rendered only when the backend names
              them.
            */}
            {commandCenter?.ai_readiness?.scope_ai ? (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Scope AI</span>
                <span className="font-bold capitalize" data-testid="readiness-scope-ai">
                  {commandCenter.ai_readiness.scope_ai}
                </span>
              </div>
            ) : null}
            {commandCenter?.ai_readiness?.risk_alerts ? (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Risk alerts</span>
                <span className="font-bold capitalize" data-testid="readiness-risk-alerts">
                  {commandCenter.ai_readiness.risk_alerts}
                </span>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* Risk / Approval Queue */}
      <Card className="stat-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-400" /> Risk / Approval Queue</CardTitle>
        </CardHeader>
        <CardContent>
          {(riskQueue?.items || []).length > 0 ? (
            <div className="space-y-3">
              {riskQueue.items.slice(0, 8).map((item) => (
                <div key={`${item.type}-${item.id}`} className="rounded-lg bg-muted/30 p-3 flex flex-col md:flex-row md:items-center justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={item.severity === "high" ? "text-red-400 font-semibold" : "text-amber-400 font-semibold"}>{item.severity}</span>
                      <p className="font-medium">{item.title}</p>
                    </div>
                    <p className="text-xs text-muted-foreground capitalize">{item.type.replace(/_/g, " ")} • {item.project_name || "No project"} • {item.status?.replace(/_/g, " ")}</p>
                  </div>
                  {item.due_date && <span className="text-xs text-muted-foreground">{new Date(item.due_date).toLocaleDateString()}</span>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No current risk or approval items.</p>
          )}
        </CardContent>
      </Card>

      {/* Recent Projects */}
      <Card className="stat-card" data-testid="recent-projects-card">
        <CardHeader>
          <CardTitle className="font-heading">Recent Projects</CardTitle>
        </CardHeader>
        <CardContent>
          {recentProjects.length > 0 ? (
            <div className="space-y-4">
              {/*
                flex-wrap and min-w-0, not a narrower column.

                The progress cell grew when it started carrying "Not
                established" instead of "62%", and the row was a non-wrapping
                flex: at 412px the right-hand cluster landed at x=433 and
                pushed a 22px column off the screen. Squeezing the cell until
                that phrase fits would just re-break the next time the wording
                changes. The row wraps instead, and the name may shrink.
              */}
              {recentProjects.map((project) => (
                <div
                  key={project.id}
                  className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer group"
                  onClick={() => navigate(`/projects/${project.id}`)}
                >
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="h-10 w-10 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate group-hover:text-primary transition-colors">{project.name}</p>
                      <p className="text-sm text-muted-foreground capitalize truncate">
                        {project.stage?.replace(/_/g, ' ')} • {project.status?.replace(/_/g, ' ')}
                      </p>
                    </div>
                  </div>
                  <div className="flex min-w-0 flex-1 items-center justify-end gap-4">
                    <div className="w-32 sm:w-40">
                      <ProjectProgress
                        testId={`recent-project-progress-${project.id}`}
                        progress={projectProgress(project)}
                      />
                    </div>
                    <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No projects yet. Create your first project to get started!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/*
          The old backend's "100% On Schedule" placeholder is never read.

          The #92 backend instead sends a structured schedule_adherence
          measurement. Its current not_computed refusal remains a refusal; a
          future established measurement can render without trusting the old
          scalar during version skew.

          This is a REFUSAL, not a derivation: the frontend does not compute
          schedule adherence, does not estimate it, and does not hide the gap.
          When the backend reports a real measurement, scheduleAdherence() is
          the one function that changes.
        */}
        {schedule.established ? (
          <Card className="stat-card" data-testid="schedule-adherence">
            <CardContent className="p-6 space-y-1">
              <p className="text-sm text-muted-foreground font-medium">On Schedule</p>
              <p className="text-2xl font-bold" data-testid="schedule-adherence-headline" data-established="true">
                {schedule.headline}
              </p>
              {schedule.basis ? (
                <p className="text-xs text-muted-foreground" data-testid="schedule-adherence-detail">
                  Measured by the configured schedule basis.
                </p>
              ) : null}
            </CardContent>
          </Card>
        ) : (
          <AbsenceNotice
            testId="schedule-adherence"
            title="On Schedule"
            headline={schedule.headline}
            detail={schedule.detail}
          />
        )}

        <Card className="stat-card" data-testid="budget-position">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                {budget.established ? (
                  <p className="text-2xl font-bold" data-testid="budget-position-value" data-established="true">
                    {Math.round(budget.value)}%
                  </p>
                ) : (
                  <p
                    className="text-base text-muted-foreground italic"
                    data-testid="budget-position-value"
                    data-established="false"
                  >
                    Not established
                  </p>
                )}
                <p className="text-sm text-muted-foreground">On Budget</p>
                {budget.detail ? (
                  <p className="text-xs text-muted-foreground mt-1" data-testid="budget-position-detail">
                    {budget.detail}
                  </p>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>

        <CommandStat
          testId="stat-in-progress"
          label="In Progress"
          fact={summary.readable ? summary.stats.activeProjects : null}
          icon={<AlertTriangle className="h-5 w-5 text-amber-500" />}
        />
      </div>
    </div>
  );
};

export default DashboardPage;
